// src/hooks/useRegions.ts
//
// React data hook for the region hierarchy editor. Plain
// useState/useCallback/useEffect per the repo's hook style (no React
// Query); all data access goes through regionsService. Mutations
// refetch rather than update optimistically — the tree re-renders only
// from the server's answer.

import { useState, useEffect, useCallback } from 'react'
import { regionsService } from '../services/regionsService'
import type { YakRegion } from '../types/yak'

interface UseRegionsReturn {
  regions: YakRegion[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  create: (name: string, parentRegionId: string | null) => Promise<void>
  rename: (id: string, name: string) => Promise<void>
  move: (id: string, newParentRegionId: string | null) => Promise<void>
  remove: (id: string) => Promise<void>
}

export function useRegions(): UseRegionsReturn {
  const [regions, setRegions] = useState<YakRegion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRegions = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      setRegions(await regionsService.listRegions())
    } catch (err) {
      // A silent refresh runs after a mutation that already succeeded on the
      // server. Surfacing its failure as the page-level error banner would
      // contradict that success and bait the user into re-submitting (which
      // then hits a duplicate-name error). Only the initial/explicit load
      // owns the banner; a failed background refresh leaves the last-known
      // tree in place and reconciles on the next successful fetch.
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load regions')
      } else {
        console.error('Region list refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRegions()
  }, [fetchRegions])

  const create = useCallback(
    async (name: string, parentRegionId: string | null) => {
      await regionsService.createRegion(name, parentRegionId)
      await fetchRegions(true)
    },
    [fetchRegions]
  )

  const rename = useCallback(
    async (id: string, name: string) => {
      await regionsService.renameRegion(id, name)
      await fetchRegions(true)
    },
    [fetchRegions]
  )

  const move = useCallback(
    async (id: string, newParentRegionId: string | null) => {
      await regionsService.moveRegion(id, newParentRegionId)
      await fetchRegions(true)
    },
    [fetchRegions]
  )

  const remove = useCallback(
    async (id: string) => {
      await regionsService.deleteRegion(id)
      await fetchRegions(true)
    },
    [fetchRegions]
  )

  return {
    regions,
    loading,
    error,
    refetch: () => fetchRegions(),
    create,
    rename,
    move,
    remove,
  }
}
